import {
  DropdownPanel,
  MenuOption,
  MenuOptionWrapper,
  PlainTextField,
} from "@smartsuite/react-ui/lib";
import { type MutableRefObject, useCallback, useEffect, useRef } from "react";
import type { UseFormFieldResult } from "../../../../hooks/useFormField";
import { autocompleteTypesMap } from "../address.constants";
import { getPlaceSuggestionDisplay } from "../address.helpers";
import type { AddressFieldTypes } from "../address.types";
import "./AutocompleteAddressField.sass";
import usePlacesAutocomplete from "../../../../hooks/usePlacesAutocomplete";
import { inputStylesWhiteNew } from "../../fields.constants";

export type AutocompleteAddressFieldProps = {
  /** When provided and single-line mode, input shows this instead of formField.value (so form can store parsed parts). */
  displayValue?: string;
  formField: UseFormFieldResult<string>;
  formFieldType: AddressFieldTypes;
  isLoaded: boolean;
  isMultiple: boolean;
  placeholder?: string;
  readOnly?: boolean;

  /** Single-line only: called when the user edits the input by hand, so the parent can reset the
   *  parsed sub-fields and keep the stored value consistent with the visible text. */
  onManualChange?: () => void;

  onSelectSuggestion: (
    suggestion: google.maps.places.AutocompleteSuggestion,
    formField: UseFormFieldResult<string>,
    formFieldType: AddressFieldTypes,
    isMultiple: boolean,
  ) => Promise<void>;
};

export const AutocompleteAddressField: React.FunctionComponent<AutocompleteAddressFieldProps> = ({
  displayValue,
  formField,
  formFieldType,
  isLoaded,
  isMultiple,
  placeholder,
  readOnly,
  onManualChange,
  onSelectSuggestion,
}) => {
  const dropdownTargetRef = useRef<HTMLInputElement | null>(null);

  const {
    init,
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    initOnMount: false,
    requestOptions: {
      includedPrimaryTypes: autocompleteTypesMap.get(formFieldType) ?? ["geocode"],
    } as Omit<google.maps.places.AutocompleteRequest, "input">,
    debounce: 300,
  });

  useEffect(() => {
    if (isLoaded) init();
  }, [init, isLoaded]);

  useEffect(() => {
    if (displayValue === undefined) setValue(formField.value);
  }, [formField.value, setValue, displayValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      formField.onChange(e);
      // Single-line renders a join of all sub-fields; a manual edit must reset the parsed parts,
      // otherwise the join re-appends them to the freshly typed text and duplicates the value.
      onManualChange?.();
    },
    [formField, setValue, onManualChange],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      formField.onBlur(e);
    },
    [formField],
  );

  const handleFocus = useCallback(() => {
    formField.onFocus();
  }, [formField]);

  const handleSelectSuggestion = (suggestion: google.maps.places.AutocompleteSuggestion): void => {
    const display = getPlaceSuggestionDisplay(suggestion);
    if (!isMultiple && display?.fullText) {
      setValue(display.fullText, false);
    }

    clearSuggestions();
    void onSelectSuggestion(suggestion, formField, formFieldType, isMultiple);
  };

  return (
    <>
      <PlainTextField
        className="autocomplete-address-field__input"
        disabled={!ready || readOnly}
        name={formField.name}
        background="white-new"
        inputStyles={inputStylesWhiteNew}
        placeholder={placeholder}
        ref={dropdownTargetRef}
        state={formField.state}
        value={displayValue !== undefined && !isMultiple ? displayValue : value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
      <DropdownPanel
        at="left bottom+4"
        my="left top"
        className="autocomplete-address-field__dropdown"
        isActive={status === "OK" && formField.state === "active"}
        targetElementRef={dropdownTargetRef as MutableRefObject<HTMLElement | undefined>}
      >
        <MenuOptionWrapper maxHeight={300}>
          {data.map((suggestion) => {
            const display = getPlaceSuggestionDisplay(suggestion);
            if (!display) return null;
            return (
              <MenuOption key={display.placeId} onClick={() => handleSelectSuggestion(suggestion)}>
                <strong>{display.mainText}</strong> <small>{display.secondaryText}</small>
              </MenuOption>
            );
          })}
        </MenuOptionWrapper>
      </DropdownPanel>
    </>
  );
};
